import React, { useState } from 'react';
import Markdown from 'markdown-to-jsx';
import styles from './advert-banner.module.scss';
import Button from '../../atoms/button/button';
import IButton from '../../../models/button/IButton';
import { ApplicationBranding } from '../../../models/settings';
import { ButtonStyleType } from '../../../constants/button-style-type';
import { ButtonSizeType } from '../../../constants/button-size-type';
import IAdvertBanner from '../../../models/advert-banner/IAdvertBanner';
import hexToRGB from '../../../utils/string-helpers/hexToRgb';
import { convertCloudinaryImage } from '../../../utils/cloudinary/cloudinary-image-url-processor';
import { replaceAttrSpaceWithHyphen } from '../../../utils/string-helpers/replace-attr-space-with-hyphen';
import colorStyle from '../../../styles/utils/variables.module.scss';
import { MediaAssetType } from '../../../constants/media-asset-type';
import useWindowResize from '../../../hooks/use-window-resize.hook';
import { deviceDetect } from '../../../data/device-detect';
import { Breakpoint } from '../../../constants/breakpoints';

type AdvertBannerProps = {
  advertBanner: IAdvertBanner;
  appBranding: ApplicationBranding;
  application: string;
};

export default function AdvertBanner({
  advertBanner,
  appBranding,
  application,
}: AdvertBannerProps) {
  const [isBreakpointUp, setIsBreakpointUp] = useState(deviceDetect.isDesktop);

  useWindowResize((newWidth) => setIsBreakpointUp(newWidth >= Breakpoint.XLarge), 100);

  if (!advertBanner) {
    return null;
  }

  if (
    advertBanner.application !== application &&
    advertBanner.application !== MediaAssetType.Both
  ) {
    return null;
  }

  let paragraphContainerBgColor = `rgba(240, 240, 240, 0.8)`;
  if (
    advertBanner.paragraphContainerColor.length > 0 &&
    advertBanner.paragraphContainerColorOpacity > -1
  )
    paragraphContainerBgColor = hexToRGB(
      advertBanner.paragraphContainerColor,
      advertBanner.paragraphContainerColorOpacity,
    );

  const cloudinaryPreprocessedBgImageUrl = convertCloudinaryImage(
    advertBanner.backgroundImage.fields.image[0].secure_url,
    1303,
    75,
    `jpg`,
  );

  let buttonStyle;
  const isButtonOutlineStyled = buttonStyle === ButtonStyleType.Outline;

  const advertCTA: IButton = {
    buttonID: advertBanner.callToAction.fields.id
      ? advertBanner.callToAction.fields.id
      : replaceAttrSpaceWithHyphen(advertBanner.callToAction.fields.buttonName),
    buttonURL: advertBanner.callToAction.fields.url,
    buttonNewTab: advertBanner.callToAction.fields.target,
    buttonAriaLabel: advertBanner.callToAction.fields.buttonName,
    buttonName: advertBanner.callToAction.fields.buttonName,
    copyColor: isButtonOutlineStyled ? appBranding.primaryColor : colorStyle.white,
    backgroundColor: isButtonOutlineStyled ? colorStyle.white : appBranding.primaryColor,
    outlineColor: appBranding.primaryColor,
    hoverBackgroundColor: appBranding.primaryHoverColor,
    activeBackgroundColor: appBranding.primaryActiveColor,
    children: `${advertBanner.callToAction.fields.text}`,
    buttonType: 'button',
    buttonSize: advertBanner.callToAction.fields.buttonSize as ButtonSizeType,
    buttonStyle: advertBanner.callToAction.fields.buttonStyle as ButtonStyleType,
    heapIdentifier: advertBanner.callToAction.fields.id
      ? advertBanner.callToAction.fields.id
      : replaceAttrSpaceWithHyphen(advertBanner.callToAction.fields.buttonName),
  };

  return (
    <section className='section-separator' id={advertBanner.id}>
      <div className='row'>
        <div className='col'>
          <div className={`${styles[`advert-banner`]}`}>
            {!isBreakpointUp && cloudinaryPreprocessedBgImageUrl && (
              <div
                className={`${styles[`advert-banner-mobile`]} d-flex align-items-stretch`}
                style={{
                  backgroundColor: advertBanner.backgroundImageColor ?? `#fff`,
                  backgroundImage: `url(${cloudinaryPreprocessedBgImageUrl})`,
                }}
              />
            )}
            {cloudinaryPreprocessedBgImageUrl && (
              <div
                className={`${styles[`banner-image`]} d-flex align-items-stretch`}
                style={{
                  backgroundColor: advertBanner.backgroundImageColor ?? `#fff`,
                  backgroundImage: `url(${cloudinaryPreprocessedBgImageUrl})`,
                }}
              >
                {advertBanner.paragraph && (
                  <div
                    className={`${styles[`paragraph-container`]} d-flex flex-column`}
                    style={{
                      backgroundColor: paragraphContainerBgColor,
                    }}
                  >
                    <h3>{advertBanner.headline}</h3>
                    <div className='mt-3'>
                      <Markdown>{advertBanner.paragraph}</Markdown>
                    </div>
                    <div className={`${styles[`paragraph-button`]}`}>
                      {advertCTA && (
                        <div className='mt-3'>
                          <Button
                            buttonID={advertCTA.buttonID}
                            buttonURL={advertCTA.buttonURL}
                            buttonNewTab={advertCTA.buttonNewTab}
                            buttonAriaLabel={advertCTA.buttonAriaLabel}
                            buttonRole={advertCTA.buttonRole}
                            buttonName={advertCTA.buttonName}
                            buttonSize={advertCTA.buttonSize}
                            buttonStyle={advertCTA.buttonStyle}
                            copyColor={
                              advertCTA.buttonStyle === ButtonStyleType.Outline
                                ? appBranding.primaryColor
                                : colorStyle.white
                            }
                            backgroundColor={
                              advertCTA.buttonStyle === ButtonStyleType.Outline
                                ? colorStyle.white
                                : appBranding.primaryColor
                            }
                            outlineColor={appBranding.primaryColor}
                            hoverBackgroundColor={appBranding.primaryHoverColor}
                            activeBackgroundColor={appBranding.primaryActiveColor}
                            buttonType={advertCTA.buttonType}
                          >
                            {advertCTA.children}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
